import { Component, OnInit } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Permission, PermissionSubject, PermissionVerb } from '@s8l/client-tree-lib';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable } from 'rxjs';
import Swal from 'sweetalert2';

import { Booking, BookingEvent, BookingServerService } from 'src/app/services/jsonrpc/booking-server.service';
import { CmsService } from 'src/app/services/cms.service';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-events',
  templateUrl: './events.component.html',
  styleUrls: ['./events.component.scss']
})
export class EventsComponent implements OnInit {
  public readonly dateTimeFormat = environment.dateTimeFormat;

  public eventSubject: BehaviorSubject<BookingEvent[]> = new BehaviorSubject<BookingEvent[]>([]);
  public events: Observable<BookingEvent[]>;
  public bookings: Promise<Booking[]>;
  public updating = false;

  public get canEdit(): boolean {
    return this.cms.hasAccess(new Permission(new PermissionSubject('booking'), PermissionVerb.Write));
  }

  public get canDelete(): boolean {
    return this.cms.hasAccess(new Permission(new PermissionSubject('booking'), PermissionVerb.Delete));
  }

  public deleteSwalOptions(entry: any) {
    return Swal.fire({
      title: 'Are you sure?',
      text: "You won't be able to revert this!",
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      buttonsStyling: false,
      customClass: {
        confirmButton: 'btn btn-primary me-1',
        cancelButton: 'btn btn-danger'
      }
    }).then(result => {
      if (result.isConfirmed) {
        return this.delete(entry);
      }
    });
  }

  public get title(): string {
    const action = this.eventModel.id ? 'Update' : 'Create';
    return `${action} Event`;
  }

  public eventModel: any = {
    startTime: { hour: 9, minute: 0, second: 0 },
    endTime: { hour: 12, minute: 0, second: 0 }
  };

  public isValidModal(): boolean {
    if (
      this.isValidName() &&
      this.isValidLocation() &&
      this.isValidCapacity() &&
      this.eventModel.endDate &&
      this.eventModel.startDate &&
      this.eventModel.startTime &&
      this.eventModel.endTime
    )
      return true;
    else return false;
  }

  public isValidName(): boolean {
    return this.isValidField(this.eventModel.name, /^\S+$/, true);
  }

  public isValidLocation(): boolean {
    return this.isValidField(this.eventModel.location, /^\S+(?: \S+)*$/, true);
  }

  public isValidCapacity(): boolean {
    return this.isValidField(this.eventModel.capacity?.toString(), /^[1-9]\d*$/, true);
  }

  public isValidStartDate(): boolean {
    return this.isValidField(this.eventModel.startDate?.toString(), /^\d{4}-\d{2}-\d{2}$/, true);
  }

  public isValidEndDate(): boolean {
    return this.isValidField(this.eventModel.endDate?.toString(), /^\d{4}-\d{2}-\d{2}$/, true);
  }

  private isValidField(value: any, pattern: RegExp, required: boolean): boolean {
    return value == null || value === '' ? !required : value.match(pattern) != null;
  }

  public get startDate(): Date {
    return new Date(
      this.eventModel.startDate.year,
      this.eventModel.startDate.month - 1,
      this.eventModel.startDate.day,
      this.eventModel.startTime.hour,
      this.eventModel.startTime.minute
    );
  }

  public get endDate(): Date {
    return new Date(
      this.eventModel.endDate.year,
      this.eventModel.endDate.month - 1,
      this.eventModel.endDate.day,
      this.eventModel.endTime.hour,
      this.eventModel.endTime.minute
    );
  }

  constructor(public cms: CmsService, public booking: BookingServerService, private modal: NgbModal, private toastr: ToastrService) {}

  public async ngOnInit() {
    this.events = this.eventSubject.asObservable();
    await this.booking.eventsList().then(b => this.eventSubject.next(b));
  }

  public open(e, content) {
    e.stopPropagation();
    const modal = this.modal.open(content, {
      size: 'lg'
    });
    return modal.result
      .then(() =>
        this.upsert({
          id: this.eventModel.id,
          name: this.eventModel.name,
          description: this.eventModel.description,
          capacity: this.eventModel.capacity,
          location: this.eventModel.location,
          startTime: this.startDate,
          endTime: this.endDate
        })
      )
      .catch(() => {});
  }

  public getBookings(id): Promise<any> {
    return (this.bookings = this.booking.bookingList({ id }));
  }

  public resetModel() {
    this.updating = false;
    this.eventModel = {};
  }

  public setModel(evt: BookingEvent) {
    this.updating = true;
    const start = new Date(evt.startTime);
    const end = new Date(evt.endTime);
    this.eventModel = {
      id: evt.id,
      name: evt.name,
      description: evt.description,
      location: evt.location,
      capacity: evt.capacity,
      endDate: {
        day: end.getUTCDate(),
        month: end.getUTCMonth() + 1,
        year: end.getUTCFullYear()
      },
      endTime: {
        hour: end.getHours(),
        minute: end.getMinutes()
      },
      startDate: {
        day: start.getUTCDate(),
        month: start.getUTCMonth() + 1,
        year: start.getUTCFullYear()
      },
      startTime: {
        hour: start.getHours(),
        minute: start.getMinutes()
      }
    };
  }

  private upsert(evt: BookingEvent) {
    if (this.updating) return this.update(evt);
    else return this.create(evt);
  }

  public create(evt: BookingEvent): Promise<any> {
    return this.createEvent(evt)
      .finally(() => (this.updating = false))
      .then(() => this.toastr.success(`event "${evt.name}" created`));
  }

  public update(evt: BookingEvent): Promise<any> {
    return this.updateEvent(evt)
      .finally(() => (this.updating = false))
      .then(() => this.toastr.success(`event "${evt.name}" updated`));
  }

  public delete(evt: BookingEvent): Promise<any> {
    return this.deleteEvent(evt.id).then(() => this.toastr.success(`event "${evt.name}" deleted`));
  }

  private createEvent(event: BookingEvent) {
    return this.booking.eventsCreate(event).then(u => {
      const old = this.eventSubject.value.filter(o => o.id !== u.id);
      this.eventSubject.next([u, ...old]);
      return this.booking.eventsList().then(events => {
        this.eventSubject.next(events);
        return event;
      });
    });
  }

  private updateEvent(evt: BookingEvent) {
    return this.booking.eventsUpdate(evt).then(u => {
      const old = this.eventSubject.value.filter(o => o.id !== u.id);
      Object.assign(evt, u);
      this.eventSubject.next([evt, ...old]);
      return this.booking.eventsList().then(events => {
        this.eventSubject.next(events);
      });
    });
  }

  private deleteEvent(id: number) {
    return new Promise<number>((resolve, reject) => {
      this.booking
        .eventsDelete({ id })
        .then(() => {
          const oldEvents = this.eventSubject.value.filter(o => o.id !== id);
          this.eventSubject.next([...oldEvents]);
          resolve(id);
        })
        .catch(error => {
          reject(error);
        });
    });
  }
}
